import { AVERAGE, BAYSIAN, PUBLISHED, ASC } from "const"
import { QidCalculationMethod } from "client/utils"
import { sort } from "client/store/utils"
import {
    ResultDatapointListType,
    QidModelList,
} from "client/store"
import { QidModel } from "server/models"


const averageBaysian = (array: number[]) => {
    // delete empty elements from an array
    const arr = array.filter((elem) => elem !== null)
    if (arr.length === 0) return null

    let arrayAvg = arr[0]
    for (let i = 1; i < arr.length; i++) {
        arrayAvg = (arrayAvg + arr[i]) / 2
    }
    return arrayAvg
}

const average = (array: number[]) => {
    // delete empty elements from an array
    const arr = array.filter((elem) => elem !== null)
    if (arr.length === 0) return null

    const sum = arr.reduce((partialSum, elem) => partialSum + elem, 0)
    return sum / arr.length
}

const parse = (value: string) => {
    const parsedValue = parseFloat(value)
    if (Number.isNaN(parsedValue)) return null
    if (parsedValue === 0) return 0
    if (parsedValue > 0) return parsedValue
    return null
}

export type QidResultType = {
    sd: number
    su: number
    dd: number
    du: number
    ne: number
    np: number
}

type ModelList = {
    modelIds: readonly number[]
    modelList: QidModelList
}
export interface CalculateQidResultProps {
    models: QidModel[] | ModelList
    datapointList: ResultDatapointListType
    method: QidCalculationMethod

    // calculation targets
    modelId: number
    safetyFunctionId: number
    optionId: number
    datapointId: number
}

export const CalculateQidResultData = ({
    models,
    datapointList,
    method,

    modelId,
    safetyFunctionId,
    optionId,
    datapointId,
}: CalculateQidResultProps) => {
    const result: QidResultType = {
        sd: null,
        su: null,
        dd: null,
        du: null,
        ne: null,
        np: null,
    }

    // only published models are used for total
    const sdTotal: number[] = []
    const suTotal: number[] = []
    const ddTotal: number[] = []
    const duTotal: number[] = []
    const neTotal: number[] = []
    const npTotal: number[] = []

    const modelArray = models as QidModel[]
    const modelObject = models as ModelList
    const targetModelArray = modelObject.modelIds ? modelObject.modelIds : modelArray

    targetModelArray.some((arrayItem) => {
        const model = Number.isNaN(Number(arrayItem))
            ? arrayItem as QidModel
            : modelObject && modelObject.modelList ? modelObject.modelList[arrayItem as number] : null

        if (!model) return false // continue script

        // for calculating final result on model Level
        let sdModel: number[] = []
        let suModel: number[] = []
        let ddModel: number[] = []
        let duModel: number[] = []
        let neModel: number[] = []
        let npModel: number[] = []

        // for calculating datapoint based on safety functions
        const sdModelFunctionBased: number[] = []
        const suModelFunctionBased: number[] = []
        const ddModelFunctionBased: number[] = []
        const duModelFunctionBased: number[] = []
        const neModelFunctionBased: number[] = []
        const npModelFunctionBased: number[] = []


        model.safetyFunctions.some((safetyFunction) => {
            // for calculating final result on safety function level
            let sdFunction: number[] = []
            let suFunction: number[] = []
            let ddFunction: number[] = []
            let duFunction: number[] = []
            let neFunction: number[] = []
            let npFunction: number[] = []

            // for calculating datapoint based on options
            const sdFunctionOptionBased: number[] = []
            const suFunctionOptionBased: number[] = []
            const ddFunctionOptionBased: number[] = []
            const duFunctionOptionBased: number[] = []
            const neFunctionOptionBased: number[] = []
            const npFunctionOptionBased: number[] = []


            safetyFunction.options.some((option) => {
                // for calculating final result on option level
                const sdOption: number[] = []
                const suOption: number[] = []
                const ddOption: number[] = []
                const duOption: number[] = []
                const neOption: number[] = []
                const npOption: number[] = []

                // get datapoints on option level
                // order by "createdAt" ASC - start calculation from the oldest to newest
                const key = `model_${model.id}_safetyFunction_${safetyFunction.id}_option_${option.id}`
                const datapointsOption = datapointList[key] && datapointList[key].datapoints
                    ? datapointList[key].datapoints.slice().sort((a, b) => sort(a, b, "createdAt", ASC))
                    : []

                datapointsOption.some((datapoint) => {
                    // for calculating average on datapoint level
                    const sdOptionDatapoint: number[] = []
                    const suOptionDatapoint: number[] = []
                    const ddOptionDatapoint: number[] = []
                    const duOptionDatapoint: number[] = []
                    const neOptionDatapoint: number[] = []
                    const npOptionDatapoint: number[] = []


                    datapoint.datapoints.forEach((child) => {
                        sdOptionDatapoint.push(parse(child.sd))
                        suOptionDatapoint.push(parse(child.su))
                        ddOptionDatapoint.push(parse(child.dd))
                        duOptionDatapoint.push(parse(child.du))
                        neOptionDatapoint.push(parse(child.ne))
                        npOptionDatapoint.push(parse(child.np))
                    })

                    // calculate average for custom failure modes
                    let sdOptionDatapointAvg = average(sdOptionDatapoint)
                    let suOptionDatapointAvg = average(suOptionDatapoint)
                    let ddOptionDatapointAvg = average(ddOptionDatapoint)
                    let duOptionDatapointAvg = average(duOptionDatapoint)
                    let neOptionDatapointAvg = average(neOptionDatapoint)
                    let npOptionDatapointAvg = average(npOptionDatapoint)

                    // calculate average with default failure mode
                    sdOptionDatapointAvg = average([sdOptionDatapointAvg, parse(datapoint.sd)])
                    suOptionDatapointAvg = average([suOptionDatapointAvg, parse(datapoint.su)])
                    ddOptionDatapointAvg = average([ddOptionDatapointAvg, parse(datapoint.dd)])
                    duOptionDatapointAvg = average([duOptionDatapointAvg, parse(datapoint.du)])
                    neOptionDatapointAvg = average([neOptionDatapointAvg, parse(datapoint.ne)])
                    npOptionDatapointAvg = average([npOptionDatapointAvg, parse(datapoint.np)])

                    sdOption.push(sdOptionDatapointAvg)
                    suOption.push(suOptionDatapointAvg)
                    ddOption.push(ddOptionDatapointAvg)
                    duOption.push(duOptionDatapointAvg)
                    neOption.push(neOptionDatapointAvg)
                    npOption.push(npOptionDatapointAvg)

                    // collecting result data
                    if (
                        model.id === modelId
                        && safetyFunction.id === safetyFunctionId
                        && option.id === optionId
                        && datapoint.id === datapointId
                    ) {
                        result.sd = sdOptionDatapointAvg
                        result.su = suOptionDatapointAvg
                        result.dd = ddOptionDatapointAvg
                        result.du = duOptionDatapointAvg
                        result.ne = neOptionDatapointAvg
                        result.np = npOptionDatapointAvg

                        return result
                    }
                    return false // continue script
                })


                let sdOptionAvg: number
                let suOptionAvg: number
                let ddOptionAvg: number
                let duOptionAvg: number
                let neOptionAvg: number
                let npOptionAvg: number

                if (method === AVERAGE) {
                    sdOptionAvg = average(sdOption)
                    suOptionAvg = average(suOption)
                    ddOptionAvg = average(ddOption)
                    duOptionAvg = average(duOption)
                    neOptionAvg = average(neOption)
                    npOptionAvg = average(npOption)
                }
                if (method === BAYSIAN) {
                    sdOptionAvg = averageBaysian(sdOption)
                    suOptionAvg = averageBaysian(suOption)
                    ddOptionAvg = averageBaysian(ddOption)
                    duOptionAvg = averageBaysian(duOption)
                    neOptionAvg = averageBaysian(neOption)
                    npOptionAvg = averageBaysian(npOption)
                }
                sdFunctionOptionBased.push(sdOptionAvg)
                suFunctionOptionBased.push(suOptionAvg)
                ddFunctionOptionBased.push(ddOptionAvg)
                duFunctionOptionBased.push(duOptionAvg)
                neFunctionOptionBased.push(neOptionAvg)
                npFunctionOptionBased.push(npOptionAvg)

                // collecting result data
                if (
                    model.id === modelId
                    && safetyFunction.id === safetyFunctionId
                    && option.id === optionId
                    && datapointId === null
                ) {
                    result.sd = sdOptionAvg
                    result.su = suOptionAvg
                    result.dd = ddOptionAvg
                    result.du = duOptionAvg
                    result.ne = neOptionAvg
                    result.np = npOptionAvg

                    return result
                }
                return false // continue script
            })


            // get datapoints on safety function level
            // order by "createdAt" ASC - start calculation from the oldest to newest
            const key = `model_${model.id}_safetyFunction_${safetyFunction.id}_option_null`
            const datapointsSafetyFunction = datapointList[key] && datapointList[key].datapoints
                ? datapointList[key].datapoints.slice().sort((a, b) => sort(a, b, "createdAt", ASC))
                : []

            datapointsSafetyFunction.some((datapoint) => {
                // for calculating average on datapoint level
                const sdFunctionDatapoint: number[] = []
                const suFunctionDatapoint: number[] = []
                const ddFunctionDatapoint: number[] = []
                const duFunctionDatapoint: number[] = []
                const neFunctionDatapoint: number[] = []
                const npFunctionDatapoint: number[] = []

                datapoint.datapoints.forEach((child) => {
                    sdFunctionDatapoint.push(parse(child.sd))
                    suFunctionDatapoint.push(parse(child.su))
                    ddFunctionDatapoint.push(parse(child.dd))
                    duFunctionDatapoint.push(parse(child.du))
                    neFunctionDatapoint.push(parse(child.ne))
                    npFunctionDatapoint.push(parse(child.np))
                })

                // calculate average for custom failure modes
                let sdFunctionDatapointAvg = average(sdFunctionDatapoint)
                let suFunctionDatapointAvg = average(suFunctionDatapoint)
                let ddFunctionDatapointAvg = average(ddFunctionDatapoint)
                let duFunctionDatapointAvg = average(duFunctionDatapoint)
                let neFunctionDatapointAvg = average(neFunctionDatapoint)
                let npFunctionDatapointAvg = average(npFunctionDatapoint)

                // calculate average with default failure mode
                sdFunctionDatapointAvg = average([sdFunctionDatapointAvg, parse(datapoint.sd)])
                suFunctionDatapointAvg = average([suFunctionDatapointAvg, parse(datapoint.su)])
                ddFunctionDatapointAvg = average([ddFunctionDatapointAvg, parse(datapoint.dd)])
                duFunctionDatapointAvg = average([duFunctionDatapointAvg, parse(datapoint.du)])
                neFunctionDatapointAvg = average([neFunctionDatapointAvg, parse(datapoint.ne)])
                npFunctionDatapointAvg = average([npFunctionDatapointAvg, parse(datapoint.np)])

                sdFunction.push(sdFunctionDatapointAvg)
                suFunction.push(suFunctionDatapointAvg)
                ddFunction.push(ddFunctionDatapointAvg)
                duFunction.push(duFunctionDatapointAvg)
                neFunction.push(neFunctionDatapointAvg)
                npFunction.push(npFunctionDatapointAvg)

                // collecting result data
                if (
                    model.id === modelId
                    && safetyFunction.id === safetyFunctionId
                    && optionId === null
                    && datapoint.id === datapointId
                ) {
                    result.sd = sdFunctionDatapointAvg
                    result.su = suFunctionDatapointAvg
                    result.dd = ddFunctionDatapointAvg
                    result.du = duFunctionDatapointAvg
                    result.ne = neFunctionDatapointAvg
                    result.np = npFunctionDatapointAvg

                    return result
                }
                return false // continue script
            })

            let sdFunctionAvg: number
            let suFunctionAvg: number
            let ddFunctionAvg: number
            let duFunctionAvg: number
            let neFunctionAvg: number
            let npFunctionAvg: number

            if (method === AVERAGE) {
                // calculate average with option based datapoints
                sdFunction = sdFunction.concat(sdFunctionOptionBased)
                suFunction = suFunction.concat(suFunctionOptionBased)
                ddFunction = ddFunction.concat(ddFunctionOptionBased)
                duFunction = duFunction.concat(duFunctionOptionBased)
                neFunction = neFunction.concat(neFunctionOptionBased)
                npFunction = npFunction.concat(npFunctionOptionBased)

                sdFunctionAvg = average(sdFunction)
                suFunctionAvg = average(suFunction)
                ddFunctionAvg = average(ddFunction)
                duFunctionAvg = average(duFunction)
                neFunctionAvg = average(neFunction)
                npFunctionAvg = average(npFunction)
            }
            if (method === BAYSIAN) {
                sdFunctionAvg = averageBaysian(sdFunction)
                suFunctionAvg = averageBaysian(suFunction)
                ddFunctionAvg = averageBaysian(ddFunction)
                duFunctionAvg = averageBaysian(duFunction)
                neFunctionAvg = averageBaysian(neFunction)
                npFunctionAvg = averageBaysian(npFunction)

                // calculate average with option based datapoints
                sdFunctionOptionBased.push(sdFunctionAvg)
                suFunctionOptionBased.push(suFunctionAvg)
                ddFunctionOptionBased.push(ddFunctionAvg)
                duFunctionOptionBased.push(duFunctionAvg)
                neFunctionOptionBased.push(neFunctionAvg)
                npFunctionOptionBased.push(npFunctionAvg)

                sdFunctionAvg = average(sdFunctionOptionBased)
                suFunctionAvg = average(suFunctionOptionBased)
                ddFunctionAvg = average(ddFunctionOptionBased)
                duFunctionAvg = average(duFunctionOptionBased)
                neFunctionAvg = average(neFunctionOptionBased)
                npFunctionAvg = average(npFunctionOptionBased)
            }

            sdModelFunctionBased.push(sdFunctionAvg)
            suModelFunctionBased.push(suFunctionAvg)
            ddModelFunctionBased.push(ddFunctionAvg)
            duModelFunctionBased.push(duFunctionAvg)
            neModelFunctionBased.push(neFunctionAvg)
            npModelFunctionBased.push(npFunctionAvg)

            // collecting result data
            if (
                model.id === modelId
                && safetyFunction.id === safetyFunctionId
                && optionId === null
                && datapointId === null
            ) {
                result.sd = sdFunctionAvg
                result.su = suFunctionAvg
                result.dd = ddFunctionAvg
                result.du = duFunctionAvg
                result.ne = neFunctionAvg
                result.np = npFunctionAvg

                return result
            }
            return false // continue script
        })


        // get datapoints on model level
        // order by "createdAt" ASC - start calculation from the oldest to newest
        const key = `model_${model.id}_safetyFunction_null_option_null`
        const datapointsModel = datapointList[key] && datapointList[key].datapoints
            ? datapointList[key].datapoints.slice().sort((a, b) => sort(a, b, "createdAt", ASC))
            : []

        datapointsModel.some((datapoint) => {
            sdModel.push(parse(datapoint.sd))
            suModel.push(parse(datapoint.su))
            ddModel.push(parse(datapoint.dd))
            duModel.push(parse(datapoint.du))
            neModel.push(parse(datapoint.ne))
            npModel.push(parse(datapoint.np))

            // collecting result data
            if (
                model.id === modelId
                && safetyFunctionId === null
                && optionId === null
                && datapoint.id === datapointId
            ) {
                result.sd = parse(datapoint.sd)
                result.su = parse(datapoint.su)
                result.dd = parse(datapoint.dd)
                result.du = parse(datapoint.du)
                result.ne = parse(datapoint.ne)
                result.np = parse(datapoint.np)

                return result
            }
            return false // continue script
        })

        let sdModelAvg: number
        let suModelAvg: number
        let ddModelAvg: number
        let duModelAvg: number
        let neModelAvg: number
        let npModelAvg: number

        if (method === AVERAGE) {
            // calculate average with function based datapoints
            sdModel = sdModel.concat(sdModelFunctionBased)
            suModel = suModel.concat(suModelFunctionBased)
            ddModel = ddModel.concat(ddModelFunctionBased)
            duModel = duModel.concat(duModelFunctionBased)
            neModel = neModel.concat(neModelFunctionBased)
            npModel = npModel.concat(npModelFunctionBased)

            sdModelAvg = average(sdModel)
            suModelAvg = average(suModel)
            ddModelAvg = average(ddModel)
            duModelAvg = average(duModel)
            neModelAvg = average(neModel)
            npModelAvg = average(npModel)
        }
        if (method === BAYSIAN) {
            sdModelAvg = averageBaysian(sdModel)
            suModelAvg = averageBaysian(suModel)
            ddModelAvg = averageBaysian(ddModel)
            duModelAvg = averageBaysian(duModel)
            neModelAvg = averageBaysian(neModel)
            npModelAvg = averageBaysian(npModel)

            // calculate average with function based datapoints
            sdModelFunctionBased.push(sdModelAvg)
            suModelFunctionBased.push(suModelAvg)
            ddModelFunctionBased.push(ddModelAvg)
            duModelFunctionBased.push(duModelAvg)
            neModelFunctionBased.push(neModelAvg)
            npModelFunctionBased.push(npModelAvg)

            sdModelAvg = average(sdModelFunctionBased)
            suModelAvg = average(suModelFunctionBased)
            ddModelAvg = average(ddModelFunctionBased)
            duModelAvg = average(duModelFunctionBased)
            neModelAvg = average(neModelFunctionBased)
            npModelAvg = average(npModelFunctionBased)
        }

        if (model.status === PUBLISHED) {
            sdTotal.push(sdModelAvg)
            suTotal.push(suModelAvg)
            ddTotal.push(ddModelAvg)
            duTotal.push(duModelAvg)
            neTotal.push(neModelAvg)
            npTotal.push(npModelAvg)
        }

        // collecting result data
        if (
            model.id === modelId
            && safetyFunctionId == null
            && optionId === null
            && datapointId === null
        ) {
            result.sd = sdModelAvg
            result.su = suModelAvg
            result.dd = ddModelAvg
            result.du = duModelAvg
            result.ne = neModelAvg
            result.np = npModelAvg

            return result
        }
        return false // continue script
    })


    const sdTotalAvg = average(sdTotal)
    const suTotalAvg = average(suTotal)
    const ddTotalAvg = average(ddTotal)
    const duTotalAvg = average(duTotal)
    const neTotalAvg = average(neTotal)
    const npTotalAvg = average(npTotal)

    // collecting result data
    if (
        modelId === null
        && safetyFunctionId === null
        && optionId === null
        && datapointId === null
    ) {
        result.sd = sdTotalAvg
        result.su = suTotalAvg
        result.dd = ddTotalAvg
        result.du = duTotalAvg
        result.ne = neTotalAvg
        result.np = npTotalAvg
    }

    return result
}
